import React, { useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../services/auth';
import { monthShortLabel } from '../incomeStatement/incomeStatement';
import { buildDashboardMonths, totalsFor, type DashboardScenario, type DashboardView } from './dashboardModel';
import type { DashboardData } from './useDashboardData';
import { DetailsView, OverviewView } from './ResultViews';
import { CostsView, OrdersView } from './OperationsViews';
import BudgetPlanEditor from '../companyBudget/BudgetPlanEditor';
import { dk, type DashboardViewContext } from './viewContext';
import { reportPeriodIndexes, type ReportPeriod } from '../reportPeriod';
import './ManagementDashboard.css';

// "Pulpit zarządu" — Emil's management dashboard concept (2026-10-13). Since
// 2026-10-26 each tab of „Wynik firmy” (/app/profitability) renders one of its
// views; since PR2 (2026-10-29) the page owns the period, the scenario and the
// loaded read models (one period for every tab), so the view has no header or
// controls of its own.

interface Props {
  view: DashboardView;
  data: DashboardData;
  period: ReportPeriod;
  /** Short name of the period („2026”, „II kw. 2026”, „paź 26”). */
  periodLabel: string;
  scenario: DashboardScenario;
  onOpenTab: (tab: string) => void;
  /** Month bar of the Pulpit: open that month's sales cut. */
  onOpenMonth?: (month: string) => void;
  /** Pulpit tiles open "Skąd ta liczba". */
  drillDown?: boolean;
  /** A section placed next to the view's first card. */
  aside?: React.ReactNode;
}

const ManagementDashboard: React.FC<Props> = ({
  view, data, period, periodLabel, scenario, onOpenTab, onOpenMonth, drillDown, aside,
}) => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'fixedCosts', 'common']);
  const language = i18n.language || 'pl';
  const { year } = period;
  const [targetsOpen, setTargetsOpen] = useState(false);

  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, index) => monthShortLabel(`${year}-${String(index + 1).padStart(2, '0')}`, language)),
    [year, language],
  );

  const ctx = useMemo<DashboardViewContext | null>(() => {
    if (!data.statement) return null;
    const statement = data.statement;
    const months = buildDashboardMonths(statement, data.budget, scenario);
    const indexes = new Set(reportPeriodIndexes(period));
    const periodMonths = months.filter((month) => indexes.has(month.index));
    return {
      t,
      language,
      year,
      scenario,
      months,
      periodMonths,
      totals: totalsFor(periodMonths),
      monthLabels,
      periodLabel,
      wholeYear: period.kind === 'year',
      statement,
      budget: data.budget,
      budgetFailed: data.errors.budget,
      liquidity: data.liquidity,
      liquidityFailed: data.errors.liquidity,
      orders: data.orders,
      quotes: data.quotes,
      ordersFailed: data.errors.orders,
      canEditTargets: authService.canEditCompanyBudget() && !data.errors.budget,
      onEditTargets: () => setTargetsOpen(true),
      monthsForScenario: (other) => buildDashboardMonths(statement, data.budget, other),
      openTab: onOpenTab,
      openMonth: onOpenMonth,
      drillDown,
      aside,
      reloadResult: data.reloadResult,
    };
  }, [aside, data.reloadResult, data.statement, data.budget, data.liquidity, data.orders, data.quotes, data.errors, scenario, period, periodLabel, year, language, monthLabels, t, onOpenTab, onOpenMonth, drillDown]);

  let content: React.ReactNode;
  if (data.errors.statement) {
    content = <Alert type="error" showIcon message={dk(t, 'errors.statement')} />;
  } else if (!ctx) {
    content = <div className="mgmt-loading"><Spin size="large" /></div>;
  } else if (view === 'orders') content = <OrdersView ctx={ctx} />;
  else if (view === 'costs') content = <CostsView ctx={ctx} />;
  else if (view === 'details') content = <DetailsView ctx={ctx} />;
  else content = <OverviewView ctx={ctx} />;

  return (
    <div className="mgmt-dashboard" data-testid={`mgmt-view-${view}`}>
      {content}
      <BudgetPlanEditor
        open={targetsOpen}
        year={year}
        budget={data.budget}
        monthLabels={monthLabels}
        onClose={() => setTargetsOpen(false)}
        onSaved={data.reloadBudget}
      />
    </div>
  );
};

export default ManagementDashboard;
